import { Component, OnInit } from '@angular/core';
import { Guess } from './models/guess.model';
import { EventsService } from '../services/events.service';
import { LookupService } from '../services/lookup.service';
import { catchError, EMPTY, Observable, take, tap } from 'rxjs';
import { NotificationService } from '../services/notification.service';
import { KeyValuePair } from '../models/key-value-pair.model';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { GameState } from './models/game-state.model';
import { GamificationService } from '../services/gamification.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ScoreCardComponent } from './score-card/score-card.component';
import { AuthService } from '../core/auth-service';

@Component({
  selector: 'app-daily-game',
  templateUrl: './daily-game.component.html',
  styleUrl: './daily-game.component.scss'
})
export class DailyGameComponent implements OnInit {
  username: string | undefined;
  loading: boolean = true;
  gameState!: GameState;
  selectedArea: number = -1;
  timer!: ReturnType<typeof setInterval>;
  gameForm!: UntypedFormGroup;
  manufacturerList: Observable<Array<KeyValuePair>> | undefined;
  modelList: Observable<Array<KeyValuePair>> | undefined;
  selectedManufacturer: string | undefined;
  selectedModel: string | undefined;
  isMakeLoading = false;
  isModelLoading = false;
  storageKey = 'GameState';
  stopping = false;

  public constructor(private eventsService: EventsService, private formBuilder: UntypedFormBuilder,
    private lookupService: LookupService, private notifications: NotificationService,
    private gamificationService: GamificationService, private modalService: NgbModal, private ppxAuthService: AuthService) {
  }

  ngOnInit(): void {
    this.username = this.ppxAuthService.getUser()?.name;
    this.setupGame();
  }

  setupGame() {
    this.gameForm = this.formBuilder.group({
      make: new UntypedFormControl({ disabled: false, value: '' }, [Validators.required]),
      model: new UntypedFormControl({ disabled: true, value: '' }, [Validators.required])
    });
    
    let usingLocalState = false;
    if (localStorage.getItem(this.storageKey)) {
      const localState = JSON.parse(localStorage.getItem(this.storageKey)) as GameState;
      if (this.isTodaysState(localState)) {
        this.gameState = localState;
        usingLocalState = true;
      }
    }
    if (!usingLocalState) {
        this.gamificationService.getGameForToday$().pipe(
          tap(result => {
            this.gameState = new GameState(this.username ?? '');
            this.gameState.setAnswers(result);
            localStorage.setItem(this.storageKey, JSON.stringify(this.gameState));
            this.populateManufacturerList();
            this.startTheGame();
          }),
          catchError(err => {
            this.notifications.dangerToast(`Failed to retrieve game settings`, err);
            return EMPTY;
          }),
          take(1)
        ).subscribe();
    } else {
      const lastGuess = this.getLastGuess();
      if (lastGuess && lastGuess.makeCorrect) {
        this.gameForm.get('make')?.setValue(lastGuess?.make);
        this.gameForm.get('make')?.disable();
        this.gameForm.get('model')?.enable();
        this.populateModelList(lastGuess?.make ?? '');
      }
      setTimeout(() => {
        for (let i = 0; i < 9; i++) {
          if (this.gameState.hiddenAreas.indexOf(i) === -1) {
            document.getElementsByClassName('blocker')[i].classList.remove('visible');
          }
        }
      }, 1000);
      this.populateManufacturerList();
      this.startTheGame();
    }
  }

  startTheGame() {
    this.loading = false;
    
    if (this.gameState.isEnded) {
      this.showFinalScore();
    } else if (this.gameState.spinning) {
      this.startTimer();
    }
  }

  private selectNext() {
    if (this.gameState.hiddenAreas.length === 1) {
      this.selectedArea = this.gameState.hiddenAreas[0];
    } else {
      let nextArea: number;
      do {
        const nextIndex = Math.floor(Math.random() * this.gameState.hiddenAreas.length);
        nextArea = this.gameState.hiddenAreas[nextIndex];
      } while (nextArea === this.selectedArea);
      this.selectedArea = nextArea;
    }
  }

  startTimer() {
    if (this.gameState.hiddenAreas.length > 0) {
      this.selectNext();
      this.timer = setInterval(() => {
        this.selectNext();
      }, 250);
    }
  }

  stop() {
    if (!this.stopping) {
      this.stopping = true;

      clearInterval(this.timer);
      
      if (this.gameState.isEnded) {
        this.showFinalScore();
        return;
      }

      document.getElementsByClassName('blocker')[this.selectedArea].classList.remove('visible');
      const selectedIndex = this.gameState.hiddenAreas.indexOf(this.selectedArea);
      this.gameState.hiddenAreas.splice(selectedIndex, 1);
      this.selectedArea = -1;
      setTimeout(() => {
        this.gameState.spinning = false;
        localStorage.setItem(this.storageKey, JSON.stringify(this.gameState));
        this.stopping = false;
      }, 250);
    }
  }

  makeChange(makeEvent) {
    if (makeEvent.target.value) {
      this.gameForm.get('make')?.setValue(makeEvent.target.value);
      this.gameForm.get('model')?.enable();
      this.selectedManufacturer = makeEvent.target.value;
      this.populateModelList(this.selectedManufacturer ?? '');
    }
  }

  modelChange(modelEvent) {
    if (modelEvent.target.value) {
      this.gameForm.get('model')?.setValue(modelEvent.target.value);
    }
  }

  attemptToWin() {
    if (this.gameState.isEnded) {
      this.showFinalScore();
      return;
    }

    const make = this.gameForm.get('make')?.value;
    const model = this.gameForm.get('model')?.value;

    const makeCorrect = this.gameState.answers.make.toLowerCase() === make.toLowerCase();
    const modelCorrect = this.gameState.answers.model.toLowerCase() === model.toLowerCase();
    this.addGuess(new Guess(make, model, makeCorrect, modelCorrect));
    if (this.gameState.isEnded) {
      if (this.gameState.isWinner) {
        this.eventsService.confettiExplosion.emit(true);
      }
      this.gamificationService.submitScore$(this.gameState.answers).pipe(
        tap(result => {
          this.gameState.answers.points = result;
          this.gameState.answers.hasPlayed = true;
          localStorage.setItem(this.storageKey, JSON.stringify(this.gameState));
          this.showFinalScore();
        }),
        catchError(err => {
          this.notifications.dangerToast(`Failed to submit score`, err);
          return EMPTY;
        }),
        take(1)
      ).subscribe();
    } else {
      if (makeCorrect) {
        this.gameForm.get('make')?.disable();
        this.gameForm.get('model')?.setValue('');
      } else {
        this.gameForm.get('make')?.setValue('');
        this.gameForm.get('model')?.setValue('');
        this.gameForm.get('model')?.disable();
        this.modelList = undefined;
      }
      this.gameState.spinning = true;
      localStorage.setItem(this.storageKey, JSON.stringify(this.gameState));
      this.startTimer();
    }
  }

  getResult(num: number, make: boolean): number {
    if (num >= this.gameState.guesses.length) {
      return 0;
    } else {
      const result = this.gameState.guesses[num];
      if (make && result.makeCorrect) {
        return 1;
      } else if (!make && result.modelCorrect) {
        return 1;
      } else {
        return -1;
      }
    }
  }

  get imageUrl(): string {
    return this.gameState.answers.imageUrl;
  }

  private populateManufacturerList() {
    const queryString = `?paramName=Manufacturer&BookId=${this.gameState.answers.bookId}`;
    this.manufacturerList = this.getManualLookupComponents(queryString, 'manufacturer');
  }

  private populateModelList(make: string) {
    const queryString = `?paramName=Model&BookId=${this.gameState.answers.bookId}&Manufacturer=${make}`;
    this.modelList = this.getManualLookupComponents(queryString, 'model');
  }

  private getManualLookupComponents(queryString: string, componentName: string): Observable<Array<KeyValuePair>> {
    return this.lookupService.getManualLookupComponents$(queryString).pipe(
      tap((result) => {
        return result;
      }),
      catchError((err) => {
        this.notifications.dangerToast('Failed to get ' + componentName, err);
        return EMPTY;
      })
    );
  }
  
  private isTodaysState(state: GameState): boolean {
    const today = new Date().toDateString();
    return today === state.playDate && state.username === this.username;
  }

  private addGuess(guess: Guess): void {
    this.gameState.guesses.push(guess);
    this.gameState.isWinner = guess.makeCorrect && guess.modelCorrect;
    this.gameState.isEnded = this.gameState.isWinner || this.gameState.guesses.length >= 9; 
    this.gameState.answers.attempts = this.gameState.guesses.length;
    this.gameState.answers.winner = this.gameState.isWinner;
    localStorage.setItem(this.storageKey, JSON.stringify(this.gameState));
  }

  getLastGuess(): Guess | undefined {
    if (this.gameState.guesses.length === 0) {
      return undefined;
    } else {
      return this.gameState.guesses[this.gameState.guesses.length - 1];
    }
  }

  showFinalScore() {
    this.gameForm.get('make')?.disable();
    this.gameForm.get('model')?.disable();
    this.gameState.spinning = false;
    
    const modalRef = this.modalService.open(ScoreCardComponent, {
      keyboard: false,
      backdrop: 'static',
      centered: true,
      size: 'md',
      modalDialogClass: 'white-modal'
    });
    modalRef.componentInstance.results = this.gameState.answers;
  }
}
